// 9fbef606107a605d69c0edbcd8029e5d
/** @module Modal
 *  @summary Wrapper component for react-modal.
 *  Accepts modal content as children and
 *  Other properties of modal to function.
 */

import React from 'react';
import { connect } from 'react-redux';
import ReactModal from 'react-modal';
import PropTypes from 'prop-types';
import { isMobileWeb, isAppleDevice } from '@tcp/core/src/utils';
import Grid from '@tcp/core/src/components/common/molecules/Grid';
import Row from '../../../atoms/Row';
import Col from '../../../atoms/Col';
import ModalHeader from './ModalHeader';
import styles from '../Modal.style';
import Config from '../Modal.config';
import withStyles from '../../../hoc/withStyles';
import { parseBoolean } from '../../../../../utils';
import { getOrderDetailsRecommendations } from '../../../../../reduxStore/selectors/session.selectors';

class Modal extends React.PureComponent {
  componentDidMount() {
    // Make sure to bind modal to your appElement (http://reactcommunity.org/react-modal/accessibility/)
    const rootElemId = '__next';
    if (process.env.NODE_ENV !== 'test' && document.getElementById(rootElemId)) {
      ReactModal.setAppElement(`#${rootElemId}`);
    }
  }

  onAfterClose = () => {
    const lastScrollPosition = window?.scrollY;
    const { isBgScrollDisabled, onAfterClose, orderDetailsRecommendationsEnabled } = this.props;
    if (isBgScrollDisabled && !document.getElementsByClassName('ReactModalPortal')[0]) {
      document.body.classList.remove('stop-body-scroll', 'ReactModal__Body--open');
    }
    if (orderDetailsRecommendationsEnabled && isMobileWeb() && isAppleDevice()) {
      window.scrollTo(0, lastScrollPosition);
    }
    if (onAfterClose) {
      onAfterClose();
    }
  };

  render() {
    const { children, onAfterOpen, onAfterClose, isBgScrollDisabled, ...otherProps } = this.props;
    const {
      colSet,
      onRequestClose,
      title,
      heading,
      fixedWidth,
      className,
      dataLocator,
      dataLocatorHeader,
      closeIconDataLocator,
      headingStyle,
      closeIconLeftAligned,
      ariaLabelledby,
      ariaDescribedby,
      aria,
      innerContentClassName = '',
      overlayClassName,
      isOpen,
      inheritedStyles,
      isSafari,
      hideCloseButton,
      isBackButtonShow,
      isBackToQuickViewButtonShow,
    } = otherProps;
    const column = colSet || Config.MODAL_COL_DEFAULTS;
    const hiddenHeaderUniqueId = `modalLabel_${Date.now()}`;
    return isOpen ? (
      <ReactModal
        {...otherProps}
        onAfterClose={this.onAfterClose}
        onAfterOpen={() => {
          if (isBgScrollDisabled) {
            document.body.classList.add('stop-body-scroll');
          }
          if (onAfterOpen) {
            onAfterOpen();
          }
        }}
        overlayClassName={`${className} ${overlayClassName}`}
        aria={{
          labelledby:
            ariaLabelledby || aria.ariaLabelledby || (heading && hiddenHeaderUniqueId) || '',
          describedby: ariaDescribedby || aria.ariaDescribedby,
          modal: true,
        }}
      >
        <span id={hiddenHeaderUniqueId} className="TCPModal__hidden_header_label">
          {heading}
        </span>
        {!fixedWidth && (
          <Grid>
            <Row>
              <Col
                colSize={column}
                className={`TCPModal__InnerContent ${innerContentClassName}`}
                data-locator={dataLocator}
              >
                <ModalHeader
                  hideCloseButton={hideCloseButton}
                  closeFunc={onRequestClose}
                  title={title}
                  heading={heading}
                  closeIconDataLocator={closeIconDataLocator}
                  closeIconLeftAligned={closeIconLeftAligned}
                  dataLocatorHeader={dataLocatorHeader}
                  headingStyle={headingStyle}
                  inheritedStyles={inheritedStyles}
                  isSafari={isSafari}
                  isBackButtonShow={isBackButtonShow}
                  isBackToQuickViewButtonShow={isBackToQuickViewButtonShow}
                />
                {children}
              </Col>
            </Row>
          </Grid>
        )}
        {fixedWidth && (
          <div
            className={`TCPModal__InnerContent ${innerContentClassName}`}
            data-locator={dataLocator}
          >
            <ModalHeader
              hideCloseButton={hideCloseButton}
              closeFunc={onRequestClose}
              title={title}
              heading={heading}
              closeIconDataLocator={closeIconDataLocator}
              closeIconLeftAligned={closeIconLeftAligned}
              dataLocatorHeader={dataLocatorHeader}
              headingStyle={headingStyle}
              inheritedStyles={inheritedStyles}
              isBackButtonShow={isBackButtonShow}
              isBackToQuickViewButtonShow={isBackToQuickViewButtonShow}
            />
            {children}
          </div>
        )}
      </ReactModal>
    ) : null;
  }
}

Modal.propTypes = {
  children: PropTypes.element.isRequired,
  aria: PropTypes.shape({}),
  spc: PropTypes.bool,
  isSafari: PropTypes.bool,
  hideCloseButton: PropTypes.bool,
};

Modal.defaultProps = {
  aria: {},
  spc: false,
  isSafari: false,
  hideCloseButton: false,
};

const mapStateToProps = (state) => {
  return {
    isBgScrollDisabled: parseBoolean(
      state &&
        state.session &&
        state.session.siteDetails &&
        state.session.siteDetails.IS_BACKGROUND_SCROLL_DISABLED
    ),
    orderDetailsRecommendationsEnabled: getOrderDetailsRecommendations(state),
  };
};

// TODO removed errorBoundary from modal as its not working properly right now
export default connect(mapStateToProps)(withStyles(Modal, styles));
export { Modal as ModalVanilla };
